import BigNumber from 'bignumber.js'

import { ReserveStatus } from '../market-info/reserve-status'
import { NormalizedUnitNumber } from '../types/NumericValues'
import { TokenSymbol } from '../types/TokenSymbol'
import { MarketInfo } from '../market-info/marketInfo'

export interface GetRepayMaxValueParams {
  user: {
    debt: NormalizedUnitNumber
    balance: NormalizedUnitNumber
    collateralAmount: NormalizedUnitNumber
  }
  asset: {
    status: ReserveStatus
    isNativeAsset: boolean
    debtToken: TokenSymbol
    collateralToken?: TokenSymbol
  }
  chain: {
    minRemainingNativeAsset: NormalizedUnitNumber
  }
  marketInfo: MarketInfo
}

export function getRepayMaxValue({ user, asset, chain, marketInfo }: GetRepayMaxValueParams): NormalizedUnitNumber {
  if (asset.status === 'paused') {
    return NormalizedUnitNumber(0)
  }

  const debtToken = marketInfo.findOneTokenBySymbol(asset.debtToken)

  let debtToRepay = user.debt

  if (asset.collateralToken && user.collateralAmount.isGreaterThan(0)) {
    const collateralToken = marketInfo.findOneTokenBySymbol(asset.collateralToken)

    const collateralValueInUsd = user.collateralAmount.multipliedBy(collateralToken.unitPriceUsd)
    const debtEquivalentFromCollateral = collateralValueInUsd.dividedBy(debtToken.unitPriceUsd)
    debtToRepay = NormalizedUnitNumber(debtToRepay.minus(debtEquivalentFromCollateral))
  }

  const maxRepay = BigNumber.min(
    debtToRepay,
    user.balance.minus(asset.isNativeAsset ? chain.minRemainingNativeAsset : NormalizedUnitNumber(0)),
  )

  // Cap the returned value at the token’s native precision.
  // If the computed max is smaller than the smallest unit
  // (e.g. < 10⁻⁶ for a 6‑dec token) return zero
  const sanitizedMaxRepay = BigNumber.max(maxRepay, 0)
  const minRepayableUnit = debtToken.decimals > 0 ? BigNumber(1).shiftedBy(-debtToken.decimals) : BigNumber(1)

  if (sanitizedMaxRepay.lt(minRepayableUnit)) {
    return NormalizedUnitNumber(0)
  }

  const alignedMaxRepay = sanitizedMaxRepay.decimalPlaces(debtToken.decimals, BigNumber.ROUND_DOWN)

  return NormalizedUnitNumber(alignedMaxRepay)
}
